// src/components/SessionCountdown.jsx
// The last 5 minutes before the inactivity sign-out: a pop-up with a live
// countdown, "Stay signed in" and "Sign out now". It stays until one of
// them is pressed (or the portal is used in another tab); moving the mouse
// doesn't close it. The browser tab's title flashes the countdown for
// someone working in another tab or window, and on coming back the pop-up
// pulses with "Stay signed in" ready to press.
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { passkeyReady, unlockWord } from '../utils/passkeys';
import Emo from './Emo';

export default function SessionCountdown() {
  const { user, showWarning, sessionEndsAt, staySignedIn, logout } = useAuth();
  // 3.69.0: with Face ID / fingerprint unlock on this device it locks instead
  const locks = showWarning && passkeyReady(user?.email);
  const [, setTick] = useState(0);   // re-render every second
  const stayRef = useRef(null);
  const dialogRef = useRef(null);
  const title = useRef(document.title);

  useEffect(() => {
    if (!showWarning) return;
    title.current = document.title;
    stayRef.current?.focus();
    let flash = false;
    const tick = () => {
      const ms = Math.max(0, sessionEndsAt() - Date.now());
      setTick(n => n + 1);
      // Alternates while the tab is in the background, to catch the eye
      flash = document.hidden && !flash;
      document.title = ms <= 0 ? `${locks ? 'Locking' : 'Signing out'} · Fax Portal`
        : flash ? `(${fmt(ms)}) Are you still there? · Fax Portal`
        : `(${fmt(ms)}) ${locks ? 'Locking' : 'Signing out'} soon · Fax Portal`;
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => { clearInterval(t); document.title = title.current; };
  }, [showWarning, sessionEndsAt, locks]);

  // Back on this tab or window while the pop-up shows: pulse it and put
  // the focus on "Stay signed in"
  useEffect(() => {
    if (!showWarning) return;
    let last = 0;
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - last < 1000) return;   // visibilitychange and focus together
      last = now;
      stayRef.current?.focus();
      const el = dialogRef.current;
      if (!el || !el.animate) return;
      const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      el.animate(still ? [
        { boxShadow: SHADOW }, { boxShadow: `${SHADOW}, 0 0 0 6px rgb(var(--c-e65100-rgb) / 0.55)` }, { boxShadow: SHADOW },
      ] : [
        { transform: 'scale(1)' }, { transform: 'scale(1.06)', boxShadow: `${SHADOW}, 0 0 0 6px rgb(var(--c-e65100-rgb) / 0.45)` },
        { transform: 'translateX(-8px)' }, { transform: 'translateX(8px)' }, { transform: 'translateX(-5px)' },
        { transform: 'translateX(5px)' }, { transform: 'none' },
      ], { duration: still ? 1200 : 700, easing: 'ease-out' });
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [showWarning]);

  if (!showWarning) return null;
  const left = Math.max(0, sessionEndsAt() - Date.now());
  return (
    <div style={S.overlay}>
      <div ref={dialogRef} style={S.dialog} role="alertdialog" aria-modal="true" aria-labelledby="idle-title" aria-describedby="idle-text">
        <div style={S.icon} aria-hidden="true"><Emo e="⏱️" /></div>
        <h3 id="idle-title" style={S.title}>Are you still there?</h3>
        <div style={S.clock} aria-live="off">{fmt(left)}</div>
        {left > 0 ? (<>
          <p id="idle-text" style={S.text}>
            {locks
              ? `For your security the portal locks when this reaches zero, because it hasn't been used for a while. You can open it again with ${unlockWord()}.`
              : "For your security you'll be signed out when this reaches zero, because the portal hasn't been used for a while."}
          </p>
          <div style={S.buttons}>
            <button onClick={logout} style={S.secondary}>Sign out now</button>
            <button ref={stayRef} onClick={staySignedIn} style={S.primary}>Stay signed in</button>
          </div>
        </>) : (
          <p id="idle-text" style={S.text}>{locks ? 'Locking…' : 'Signing you out…'}</p>
        )}
      </div>
    </div>
  );
}

function fmt(ms) {
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const SHADOW = '0 20px 60px rgb(var(--c-000000-rgb) / 0.2)';
const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-000000-rgb) / 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 },
  dialog: { background: 'var(--c-ffffff)', borderRadius: 12, padding: '28px 28px 24px', maxWidth: 400, width: '100%', boxShadow: SHADOW, textAlign: 'center' },
  icon: { fontSize: 36, marginBottom: 6 },
  title: { margin: '0 0 6px', color: 'var(--t-e65100)', fontSize: 18 },
  clock: { fontSize: 40, fontWeight: 700, color: 'var(--t-263238)', fontVariantNumeric: 'tabular-nums', margin: '4px 0 8px' },
  text: { margin: '0 0 20px', color: 'var(--t-546e7a)', fontSize: 14, lineHeight: 1.6 },
  buttons: { display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' },
  primary: { padding: '10px 24px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
  secondary: { padding: '10px 18px', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', border: '1.5px solid var(--c-cfd8dc)', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
};
